import {ChevronDownIcon, ChevronRightIcon} from '@primer/octicons-react'
import {IconButton, Label} from '@primer/react'
import {clsx} from 'clsx'

import type {Thread} from '../../conversation-types'
import {CommentReviewVariantType} from '../../conversation-types'
import styles from './InlineReviewThread.module.css'

interface ReviewThreadHeaderProps {
  thread: Thread
  isCollapsed: boolean
  isFirstThread?: boolean
  onToggleCollapsed: () => void
  showLabels?: boolean
}

function ThreadBanner({thread}: {thread: Thread}) {
  const positioning = thread.positioning ?? ('subject' in thread ? thread.subject : undefined) // support both for backwards compatibility

  if (typeof positioning === 'undefined') return null

  if (!positioning.endDiffSide || !positioning.endLine) {
    return <h2 className={styles.inlineReviewThreadHeading}>Comment on file</h2>
  }

  const diffSideLetter = positioning.endDiffSide === 'LEFT' ? 'L' : 'R'

  if (!positioning.startLine || !positioning.startDiffSide || positioning.startLine === positioning.endLine) {
    return (
      <h2 className={styles.inlineReviewThreadHeading}>
        Comment on line{' '}
        <span className="text-semibold fgColor-default">
          {diffSideLetter}
          {positioning.endLine ?? ''}
        </span>
      </h2>
    )
  } else {
    const startSideString = positioning.startDiffSide === 'LEFT' ? 'L' : 'R'
    const endSideString = positioning.endDiffSide === 'LEFT' ? 'L' : 'R'
    return (
      <h2 className={styles.inlineReviewThreadHeading}>
        Comment on lines{' '}
        <span className="text-semibold fgColor-default">
          {startSideString}
          {positioning.startLine ?? ''}
        </span>{' '}
        to{' '}
        <span className="text-semibold fgColor-default">
          {endSideString}
          {positioning.endLine ?? ''}
        </span>
      </h2>
    )
  }
}

export function ReviewThreadHeader({
  thread,
  isCollapsed,
  isFirstThread,
  onToggleCollapsed,
  showLabels = true,
}: ReviewThreadHeaderProps) {
  const isFullThread = 'commentsData' in thread
  const isAutomatedReviewThread =
    isFullThread && thread.commentsData?.comments[0]?.reviewVariantType === CommentReviewVariantType.Automated
  const automatedComment = isAutomatedReviewThread ? thread.commentsData?.comments[0]?.automatedComment : null
  const isThreadResolved = isFullThread && !!thread.isResolved
  const isDismissableAutomatedComment = Boolean(automatedComment && automatedComment.dismissable)

  return (
    <div className={clsx('px-1', styles.ReviewThreadContainer)}>
      <IconButton
        aria-label={isCollapsed ? 'Expand comment' : 'Collapse comment'}
        icon={isCollapsed ? ChevronRightIcon : ChevronDownIcon}
        size="small"
        variant="invisible"
        onClick={onToggleCollapsed}
        data-is-first-collapse-button={isFirstThread}
      />
      <ThreadBanner thread={thread} />
      {showLabels && (
        <div className={styles.ResolvableContainer}>
          {thread.positioning?.isOutdated && <Label variant="secondary">Outdated</Label>}

          {isThreadResolved && !isDismissableAutomatedComment && <Label variant="secondary">Resolved</Label>}
          {automatedComment?.isDismissed && <Label variant="secondary">Dismissed</Label>}
          {automatedComment?.isFixed && <Label variant="secondary">Fixed</Label>}
        </div>
      )}
    </div>
  )
}
